import { useEffect, useMemo, useState } from "react";
import { Star, Loader2, Copy, Check, BookOpen, Flag, AlertTriangle } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiGet, v2 } from "@/modules/collector_v2/lib/apiClient";
import {
  formatProfit,
  profitTone,
  formatEpochDate,
  gameModeLabel,
  boardToString,
} from "@/modules/collector_v2/lib/replayFormatters";
import { formatNumber } from "@/modules/collector_v2/lib/formatters";
import { JsonHandHistory } from "./hand-history/JsonHandHistory";
import {
  useFlaggedUids,
  severityStarColor,
  rowTintForFlag,
  SEVERITY_LABELS,
  ROW_TINTS,
} from "@/modules/collector_v2/hooks/useFlaggedUids";

/**
 * Sheet con dos tabs: Resumen / Detalle
 * El tab Resumen incluye:
 *   - Header compacto con stats clave
 *   - Crónica narrada de la mano (con cards inline coloreadas + botón Copiar)
 *   - Jugadores con profit/posición
 */
export function HandSummaryDrawer({ handId, isCollected, onToggleCollect, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { computeRowFlag, flaggedMap } = useFlaggedUids();

  useEffect(() => {
    if (!open || !handId) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    apiGet(v2.handDetail(handId))
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled)
          setError(e?.response?.data?.detail || e.message || "Error al cargar la mano");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, handId]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="sm:max-w-2xl w-full overflow-y-auto"
        data-testid="hand-summary-drawer"
      >
        <SheetHeader className="pb-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <SheetTitle className="font-mono text-sm break-all">
                {handId || "—"}
              </SheetTitle>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => onToggleCollect?.(handId, isCollected)}
                title={isCollected ? "Quitar de colección" : "Agregar a colección"}
                data-testid="drawer-collect-btn"
                className={`h-7 w-7 shrink-0 ${isCollected ? "text-amber-500 hover:text-amber-600" : "text-muted-foreground hover:text-amber-500"}`}
              >
                <Star className="h-4 w-4" fill={isCollected ? "currentColor" : "none"} />
              </Button>
            </div>
            <SheetDescription>
              Detalle de la mano · resumen, jugadores y log de acciones.
            </SheetDescription>
          </div>
        </SheetHeader>

        {loading && (
          <div
            className="flex items-center justify-center py-12 text-muted-foreground"
            data-testid="drawer-loading"
          >
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Cargando detalle…
          </div>
        )}

        {error && (
          <div
            className="py-8 text-center text-sm text-rose-600"
            data-testid="drawer-error"
          >
            {error}
          </div>
        )}

        {!loading && !error && data && (() => {
          const playerUids = (data.players || []).map((p) => Number(p.uid)).filter((x) => x);
          const handFlag = computeRowFlag(playerUids);
          // ⭐⭐ MATCH STARS (2026-05-13): UIDs involucrados en un MATCH detectado
          // se marcan con doble estrellita amarilla en Detalle y en PlayersList.
          const matchedUidSet = new Set(
            handFlag.isMatch
              ? (handFlag.matchPairs || []).flatMap((pair) => pair.map((x) => Number(x)))
              : []
          );
          return (
          <Tabs defaultValue="summary" className="w-full">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="summary" data-testid="drawer-tab-summary">
                Resumen
              </TabsTrigger>
              <TabsTrigger value="detail" data-testid="drawer-tab-detail">
                Detalle
              </TabsTrigger>
            </TabsList>

            <TabsContent value="summary" className="mt-4 space-y-4">
              {handFlag.hasFlag && <FlagBanner flag={handFlag} />}
              <SummaryView summary={data.summary} hand={data.hand} />
              <NarrativeCard narrative={data.narrative} />
              <PlayersList players={data.players} flaggedMap={flaggedMap} matchedUidSet={matchedUidSet} />
            </TabsContent>

            <TabsContent value="detail" className="mt-4 space-y-4">
              {handFlag.hasFlag && <FlagBanner flag={handFlag} />}
              <JsonHandHistory data={data} matchedUidSet={matchedUidSet} />
            </TabsContent>
          </Tabs>
          );
        })()}
      </SheetContent>
    </Sheet>
  );
}

// ───────────────────────────── Subcomponentes ─────────────────────────────

function SummaryView({ summary, hand }) {
  if (!summary) return null;
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="hand-summary-stats">
      <Stat label="Mesa" value={summary.table_name || `#${summary.table_id}`} />
      <Stat label="Juego" value={gameModeLabel(summary.game_mode)} />
      <Stat label="Jugadores" value={formatNumber(summary.num_players)} />
      <Stat label="Big Blind" value={summary.big_blind_visual || "—"} />
      <Stat label="Pot final" value={summary.pot_size ?? "—"} />
      <Stat label="Stage final" value={summary.final_stage ?? "—"} />
      <Stat
        label="Inicio"
        value={formatEpochDate(summary.started_at_raw)}
        className="col-span-2"
      />
      <Stat
        label="Board"
        value={boardToString(summary.board)}
        mono
        className="col-span-2"
      />
      <FlagRow
        flags={[
          { label: "Bomb pot", on: hand?.is_bomb_pot },
          { label: "Double board", on: hand?.is_double_board },
          { label: "Jackpot", on: hand?.is_jackpot_hand },
          { label: "EV chop", on: hand?.is_evchop },
          { label: "Calibration", on: summary.calibration_only },
        ]}
      />
    </div>
  );
}

// ───────────────────────────── Crónica ─────────────────────────────

/**
 * Renderiza una línea de crónica con:
 *   - **negrita** entre dobles asteriscos
 *   - cartas tipo "K♥", "10♣", "A♦" coloreadas (rojo/negro)
 */
function RichLine({ text }) {
  const parts = useMemo(() => parseRichLine(text), [text]);
  return (
    <p className="text-sm leading-relaxed">
      {parts.map((p, i) => {
        if (p.type === "bold") {
          return (
            <strong key={i} className="font-semibold text-foreground">
              {p.value}
            </strong>
          );
        }
        if (p.type === "card") {
          const isRed = p.suit === "♥" || p.suit === "♦";
          return (
            <span
              key={i}
              className={`inline-flex items-center justify-center px-1.5 py-0.5 mx-[1px] rounded border text-[12px] font-mono font-semibold tabular-nums ${
                isRed
                  ? "border-rose-300 bg-rose-50 text-rose-600 dark:border-rose-700 dark:bg-rose-950/40 dark:text-rose-400"
                  : "border-slate-300 bg-slate-50 text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              }`}
            >
              {p.rank}
              {p.suit}
            </span>
          );
        }
        return <span key={i}>{p.value}</span>;
      })}
    </p>
  );
}

/**
 * Parsea una línea con marcas **negrita** y cartas Unicode "K♥", "10♣"...
 * Retorna array de {type: 'text'|'bold'|'card', value, rank, suit}.
 */
function parseRichLine(text) {
  if (!text) return [];
  // 1) Partir por ** (negrita)
  const bold = text.split(/(\*\*[^*]+\*\*)/g);
  const out = [];
  for (const seg of bold) {
    if (!seg) continue;
    if (seg.startsWith("**") && seg.endsWith("**")) {
      out.push(...splitCards(seg.slice(2, -2)).map((p) =>
        p.type === "text" ? { type: "bold", value: p.value } : p,
      ));
    } else {
      out.push(...splitCards(seg));
    }
  }
  return out;
}

function splitCards(seg) {
  // Captura grupo: rank (10 | A K Q J 2-9) + palo unicode ♥♦♣♠
  const re = /([2-9TJQKA]|10)([♥♦♣♠])/g;
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(seg)) !== null) {
    if (m.index > last) {
      out.push({ type: "text", value: seg.slice(last, m.index) });
    }
    out.push({ type: "card", rank: m[1], suit: m[2], value: m[0] });
    last = m.index + m[0].length;
  }
  if (last < seg.length) {
    out.push({ type: "text", value: seg.slice(last) });
  }
  return out;
}

function NarrativeCard({ narrative }) {
  const [copied, setCopied] = useState(false);
  if (!narrative?.lines?.length) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(narrative.plain || narrative.lines.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore: clipboard puede fallar en contextos no-https
    }
  };

  return (
    <div
      className="rounded-lg border bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 p-4 space-y-3"
      data-testid="hand-narrative-card"
    >
      <div className="flex items-center justify-between">
        <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
          <BookOpen className="h-3.5 w-3.5" />
          Crónica de la mano
        </h4>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleCopy}
          className="h-7 px-2 text-xs gap-1"
          data-testid="narrative-copy-btn"
          aria-label="Copiar crónica"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-emerald-600" />
              Copiado
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              Copiar
            </>
          )}
        </Button>
      </div>
      <div className="space-y-2">
        {narrative.lines.map((line, i) => (
          <RichLine key={i} text={line} />
        ))}
      </div>
    </div>
  );
}

// ───────────────────────────── Jugadores ─────────────────────────────

function PlayersList({ players, flaggedMap, matchedUidSet }) {
  if (!players?.length) return null;
  return (
    <div>
      <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
        Jugadores ({players.length})
      </h4>
      <div className="space-y-1 rounded-md border">
        {players.map((p) => {
          const tone = profitTone(p.profit_visual);
          const flagInfo = flaggedMap?.get?.(Number(p.uid));
          const flagBg = flagInfo
            ? flagInfo.severity === "high"
              ? "#fee2e2"
              : flagInfo.severity === "medium"
              ? "#ffedd5"
              : "#fef9c3"
            : null;
          const starColor = flagInfo ? severityStarColor(flagInfo.severity) : null;
          const tooltip = flagInfo
            ? `🚩 UID ${p.uid} · Severidad ${SEVERITY_LABELS[flagInfo.severity] || flagInfo.severity}\n` +
              `Aparece en ${flagInfo.pair_count} par(es). Vinculado con: ${flagInfo.partner_uids.join(", ")}\n` +
              `Veredictos: ${flagInfo.verdicts_in.join(", ")}`
            : undefined;
          return (
            <div
              key={p.hand_player_id || p.uid}
              className="flex items-center justify-between px-3 py-2 text-sm border-b last:border-b-0"
              data-testid="drawer-player-row"
              data-flagged-uid={flagInfo ? p.uid : ""}
              data-severity={flagInfo?.severity || ""}
              style={flagBg ? { backgroundColor: flagBg } : undefined}
              title={tooltip}
            >
              <div className="flex items-center gap-3 min-w-0">
                <Badge variant="outline" className="text-[10px] tabular-nums">
                  S{p.seat}
                </Badge>
                {flagInfo && (
                  <Star
                    className="h-4 w-4 shrink-0"
                    fill={starColor}
                    style={{ color: starColor }}
                    data-testid={`flagged-star-${p.uid}`}
                  />
                )}
                <span className="font-medium truncate">{p.nick || `uid_${p.uid}`}</span>
                {matchedUidSet?.has(Number(p.uid)) && (
                  <span
                    className="ml-1 text-amber-500 leading-none"
                    title="UID en MATCH (collusion_suspected)"
                    data-testid={`match-stars-${p.uid}`}
                  >⭐⭐</span>
                )}
                {p.position && (
                  <Badge variant="secondary" className="text-[10px]">
                    {p.position}
                  </Badge>
                )}
                {p.is_winner && (
                  <Badge className="text-[10px] bg-emerald-600 hover:bg-emerald-700">
                    WIN
                  </Badge>
                )}
              </div>
              <span
                className={`font-mono text-xs tabular-nums ${
                  tone === "positive"
                    ? "text-emerald-600"
                    : tone === "negative"
                    ? "text-rose-600"
                    : ""
                }`}
              >
                {formatProfit(p.profit_visual)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ───────────────────────────── Banner MATCH/Flag ─────────────────────────────

function FlagBanner({ flag }) {
  if (!flag?.hasFlag) return null;
  const bg = rowTintForFlag(flag) || ROW_TINTS.weak;
  const borderColor = flag.isMatch
    ? "#dc2626"
    : flag.maxSeverity === "high"
    ? "#dc2626"
    : flag.maxSeverity === "medium"
    ? "#f97316"
    : "#eab308";

  if (flag.isMatch) {
    return (
      <div
        data-testid="drawer-match-banner"
        className="rounded-md border-l-4 p-3 flex items-start gap-2"
        style={{ backgroundColor: bg, borderColor }}
      >
        <Flag className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "#b91c1c" }} />
        <div className="text-sm">
          <strong className="block text-rose-700">🚩 MATCH DETECTADO</strong>
          <span className="text-rose-900">
            UIDs vinculados juegan juntos en esta mano:{" "}
            {flag.matchPairs.map(([a, b], i) => (
              <span key={`${a}-${b}`} className="font-mono">
                {i > 0 ? " · " : ""}
                {a} ⟷ {b}
              </span>
            ))}
            . Veredicto: {flag.flaggedPlayers
              .flatMap((p) => p.verdicts_in)
              .filter((v, i, arr) => arr.indexOf(v) === i)
              .join(", ")}.
          </span>
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="drawer-flagged-banner"
      className="rounded-md border-l-4 p-3 flex items-start gap-2"
      style={{ backgroundColor: bg, borderColor }}
    >
      <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: borderColor }} />
      <div className="text-sm">
        <strong className="block">⭐ Jugador(es) en watchlist</strong>
        <span className="text-slate-700">
          {flag.flaggedPlayers.length} UID(s) flagged · severidad max:{" "}
          {SEVERITY_LABELS[flag.maxSeverity] || flag.maxSeverity} ·{" "}
          {flag.flaggedPlayers.map((p) => p.uid).join(", ")}
        </span>
      </div>
    </div>
  );
}

function Stat({ label, value, mono = false, className = "" }) {
  return (
    <div className={`rounded-md border p-3 ${className}`}>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </p>
      <p className={`text-sm font-medium mt-0.5 ${mono ? "font-mono" : ""}`}>
        {value || "—"}
      </p>
    </div>
  );
}

function FlagRow({ flags }) {
  const visible = flags.filter((f) => f.on);
  if (visible.length === 0) return null;
  return (
    <div className="col-span-2 flex flex-wrap gap-2 pt-1">
      {visible.map((f) => (
        <Badge key={f.label} variant="secondary" className="text-[10px]">
          {f.label}
        </Badge>
      ))}
    </div>
  );
}
